<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useFunctionGetter, useStore } from 'dashboard/composables/store';
import {
  parseDetailReportFilters,
  generateReportURLParams,
  generateSummaryEntityFilterParams,
} from './helpers/reportFilterHelper';
import ReportFiltersSection from './components/ReportFiltersSection.vue';
import SummaryDetailHeader from './components/SummaryDetailHeader.vue';
import SummaryDetailMetrics from './components/SummaryDetailMetrics.vue';
import SummaryEntityDetailCharts from './components/SummaryEntityDetailCharts.vue';
import SummaryDetailHeatmaps from './components/SummaryDetailHeatmaps.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const route = useRoute();
const router = useRouter();
const store = useStore();
const inbox = useFunctionGetter('inboxes/getInboxById', route.params.id);

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedTeamIds = ref([]);
const selectedAgentIds = ref([]);
const selectedLabelIds = ref([]);
const excludeLabelIds = ref([]);
const selectedChannelTypes = ref([]);
const resolutionMethod = ref('all');

const applyEntityFilters = filters => {
  selectedTeamIds.value = filters.teamIds || [];
  selectedAgentIds.value = filters.agentIds || [];
  selectedLabelIds.value = filters.labelIds || [];
  excludeLabelIds.value = filters.excludeLabelIds || [];
  selectedChannelTypes.value = filters.channelTypes || [];
  resolutionMethod.value = filters.resolutionMethod || 'all';
};

const initializeFromURL = () => {
  const parsed = parseDetailReportFilters(route.query);
  from.value = parsed.from || 0;
  to.value = parsed.to || 0;
  businessHours.value = parsed.businessHours;
  applyEntityFilters(parsed);
};

initializeFromURL();

const detailFilters = computed(() => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  teamIds: selectedTeamIds.value,
  agentIds: selectedAgentIds.value,
  labelIds: selectedLabelIds.value,
  excludeLabelIds: excludeLabelIds.value,
  inboxIds: [Number(route.params.id)],
  channelTypes: selectedChannelTypes.value,
  resolutionMethod: resolutionMethod.value,
}));

const updateURLParams = () => {
  const entityParams = generateSummaryEntityFilterParams({
    teamIds: selectedTeamIds.value,
    agentIds: selectedAgentIds.value,
    labelIds: selectedLabelIds.value,
    excludeLabelIds: excludeLabelIds.value,
    channelTypes: selectedChannelTypes.value,
    resolutionMethod: resolutionMethod.value,
  });
  const dateParams = generateReportURLParams({
    from: from.value,
    to: to.value,
    businessHours: businessHours.value,
  });
  router.replace({ query: { ...dateParams, ...entityParams } });
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  businessHours.value = date.businessHours;
  applyEntityFilters(entity);
  updateURLParams();
};

onMounted(() => store.dispatch('inboxes/get'));
watch(() => route.query, initializeFromURL);
</script>

<template>
  <div v-if="inbox.id" class="flex flex-col gap-4">
    <SummaryDetailHeader
      :title="inbox.name"
      report-type="inbox"
      :filters="detailFilters"
      :download-label="$t('INBOX_REPORTS.DOWNLOAD_INBOX_REPORTS')"
    />
    <ReportFiltersSection
      type="inbox"
      hide-inbox-filter
      @filters-apply="onFiltersApply"
    />
    <SummaryDetailMetrics
      type="inbox"
      :entity-id="inbox.id"
      :filters="detailFilters"
    />
    <SummaryEntityDetailCharts
      type="inbox"
      :entity-id="inbox.id"
      :filters="detailFilters"
    />
    <SummaryDetailHeatmaps
      entity-type="inbox"
      :entity-id="inbox.id"
      :filters="detailFilters"
    />
  </div>
  <div v-else class="w-full py-20">
    <Spinner class="mx-auto" />
  </div>
</template>
